/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * One category section in the Resources Library page. Header + 2-col
 * grid of ResourceCard.
 */

import React from 'react';
import { BookOpen, Wrench, Users, Award, Newspaper, Folder } from 'lucide-react';
import ResourceCard from './ResourceCard';

export const CATEGORY_META = {
  documentation: {
    title: 'Documentation',
    description: 'Official docs from Manhattan, IBM, Apache, and the JVM stack.',
    icon: BookOpen,
  },
  tools: {
    title: 'Tools',
    description: 'Terminals, database clients, API tools, and console utilities.',
    icon: Wrench,
  },
  community: {
    title: 'Community',
    description: 'Forums, subreddits, LinkedIn groups, and Q&A sites.',
    icon: Users,
  },
  certifications: {
    title: 'Certifications',
    description: 'Manhattan, APICS/ASCM, ISCEA, and Six Sigma credentials.',
    icon: Award,
  },
  reference: {
    title: 'Reference Reading',
    description: 'Industry reports, magazines, and canonical books.',
    icon: Newspaper,
  },
};

const ResourceCategorySection = ({ categoryKey, resources }) => {
  const meta = CATEGORY_META[categoryKey] || {
    title: categoryKey,
    description: '',
    icon: Folder,
  };
  const Icon = meta.icon;

  if (!resources || resources.length === 0) return null;

  return (
    <section aria-label={meta.title} className="mt-16">
      <header className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-cyan/10 flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-brand-cyan" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
            {meta.title}
          </h2>
          {meta.description && (
            <p className="mt-1 text-sm text-brand-muted leading-relaxed">
              {meta.description}
            </p>
          )}
        </div>
      </header>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {resources.map((r) => (
          <ResourceCard key={r.id} resource={r} />
        ))}
      </div>
    </section>
  );
};

export default ResourceCategorySection;
